<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from "@headlessui/vue";
import ButtonDefaultComponent from "@/components/Forms/Button/ButtonDefaultComponent.vue";
import ButtonStyle2Component from "@/components/Forms/Button/ButtonStyle2Component.vue";

import { ENUM_SIZE, ENUM_COLOR } from "@/configs/enums/enum";

const PopupDeleteComponent = defineComponent({
  name: "PopupDeleteComponent",
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
    ButtonDefaultComponent,
    ButtonStyle2Component,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    textTitle: {
      type: String,
      default: "typeGetTitle",
    },
    textDetail: {
      type: String,
      default: "Detail",
    },
    functionProp: {
      type: Function,
      default: () => {},
    },
    functionCancelProp: {
      type: Function,
      default: () => {},
    },
    submitEvent: {
      required: false,
      default: () => {},
    } as any,
  },
  data() {
    return {};
  },
  setup(props, { emit }) {
    const isOpenLocal = ref(props.isOpen);

    watch(
      () => props.isOpen,
      (newValue) => {
        isOpenLocal.value = newValue;
      }
    );

    function toggleOpen() {
      isOpenLocal.value = !isOpenLocal.value;
      emit("updateIsOpen", isOpenLocal.value);
    }

    onMounted(() => {
      emit("sendToggleOpen", toggleOpen);
    });

    return {
      isOpen: isOpenLocal,
      ENUM_COLOR,
      ENUM_SIZE,
    };
  },
  methods: {
    handleClick(e: Event) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (typeof this.functionProp === "function") {
        this.functionProp(e);
      }
    },
    handleCancelClick(e: Event) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (typeof this.functionCancelProp === "function") {
        this.functionCancelProp(e);
      }
    },
  },
});
export default PopupDeleteComponent;
</script>

<template>
  <div>
    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" @close="handleCancelClick" class="relative z-9999">
        <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-100" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm" />
        </TransitionChild>
        <div class="fixed inset-0 overflow-y-auto p-4 lg:p-8 ttp-fixed-tourist-margin-mobile-header">
          <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 scale-125" enter-to="opacity-100 scale-100" leave="ease-in duration-100" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-125">
            <DialogPanel class="w-full max-w-md mx-auto flex flex-col rounded-lg shadow-md bg-white overflow-hidden dark:text-gray-100 dark:bg-gray-800">
              <div class="relative p-4">
                <div class="text-right">
                  <button @click="handleCancelClick" @touchstart="handleCancelClick" type="button" class="inline-flex items-center justify-center space-x-2 rounded-lg border border-transparent px-3 py-2 ttp-text-xs font-semibold leading-5 text-gray-800 hover:text-gray-900 focus:ring focus:ring-gray-300 focus:ring-opacity-25 active:border-gray-200 active:shadow-none dark:border-transparent dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600 dark:focus:ring-opacity-40 dark:active:border-gray-700">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="Icons/Common Action/Close">
                        <path id="Vector" d="M19.7433 3.48947C19.5452 3.49414 19.357 3.577 19.2198 3.71994L12.0001 10.9397L4.78039 3.71994C4.71048 3.64794 4.62684 3.59071 4.53441 3.55163C4.44198 3.51254 4.34265 3.4924 4.2423 3.4924C4.09304 3.49243 3.94719 3.537 3.8234 3.6204C3.69961 3.70379 3.60352 3.82223 3.54741 3.96054C3.49131 4.09886 3.47775 4.25077 3.50847 4.39683C3.53918 4.5429 3.61278 4.67648 3.71984 4.78048L10.9396 12.0002L3.71984 19.2199C3.64786 19.289 3.5904 19.3718 3.55081 19.4634C3.51122 19.555 3.49031 19.6536 3.4893 19.7534C3.48828 19.8531 3.50719 19.9521 3.5449 20.0445C3.58262 20.1369 3.63839 20.2208 3.70895 20.2914C3.77951 20.3619 3.86344 20.4177 3.95582 20.4554C4.0482 20.4931 4.14718 20.512 4.24696 20.511C4.34674 20.51 4.44531 20.4891 4.53691 20.4495C4.6285 20.4099 4.71128 20.3525 4.78039 20.2805L12.0001 13.0608L19.2198 20.2805C19.289 20.3525 19.3717 20.4099 19.4633 20.4495C19.5549 20.4891 19.6535 20.51 19.7533 20.511C19.8531 20.512 19.952 20.4931 20.0444 20.4554C20.1368 20.4177 20.2207 20.3619 20.2913 20.2914C20.3618 20.2208 20.4176 20.1369 20.4553 20.0445C20.493 19.9521 20.512 19.8531 20.5109 19.7534C20.5099 19.6536 20.489 19.555 20.4494 19.4634C20.4098 19.3718 20.3524 19.289 20.2804 19.2199L13.0607 12.0002L20.2804 4.78048C20.3895 4.6758 20.4643 4.54061 20.4952 4.39263C20.5261 4.24465 20.5116 4.09079 20.4535 3.95121C20.3955 3.81164 20.2966 3.69287 20.1698 3.61047C20.0431 3.52807 19.8944 3.4859 19.7433 3.48947Z" fill="#4C4E64" fill-opacity="0.68" />
                      </g>
                    </svg>
                  </button>
                </div>
                <div class="w-14 h-14 mx-auto flex items-center justify-center">
                  <svg width="41" height="45" viewBox="0 0 41 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path id="Vector" d="M20.5 0C16.8423 0 13.8412 3.00103 13.8412 6.65876H6.90069C6.8019 6.64183 6.70179 6.63385 6.60156 6.63492C6.52089 6.63703 6.44048 6.645 6.36097 6.65876H2.1884C1.96781 6.65565 1.7488 6.6964 1.54409 6.77866C1.33939 6.86092 1.15308 6.98304 0.995979 7.13793C0.838883 7.29282 0.714137 7.47739 0.628992 7.68091C0.543847 7.88443 0.5 8.10284 0.5 8.32346C0.5 8.54407 0.543847 8.76248 0.628992 8.966C0.714137 9.16952 0.838883 9.35409 0.995979 9.50898C1.15308 9.66387 1.33939 9.786 1.54409 9.86825C1.7488 9.95051 1.96781 9.99127 2.1884 9.98815H4.96289V38.2879C4.96289 41.6392 7.71543 44.3918 11.0668 44.3918H29.9333C33.2846 44.3918 36.0371 41.6392 36.0371 38.2879V9.98815H38.8116C39.0322 9.99127 39.2512 9.95051 39.4559 9.86825C39.6606 9.786 39.8469 9.66387 40.004 9.50898C40.1611 9.35409 40.2859 9.16952 40.371 8.966C40.4562 8.76248 40.5 8.54407 40.5 8.32346C40.5 8.10284 40.4562 7.88443 40.371 7.68091C40.2859 7.47739 40.1611 7.29282 40.004 7.13793C39.8469 6.98304 39.6606 6.86092 39.4559 6.77866C39.2512 6.6964 39.0322 6.65565 38.8116 6.65876H34.6455C34.4668 6.62941 34.2845 6.62941 34.1058 6.65876H27.1588C27.1588 3.00103 24.1577 0 20.5 0ZM20.5 3.32938C22.3579 3.32938 23.8294 4.80082 23.8294 6.65876H17.1706C17.1706 4.80082 18.6421 3.32938 20.5 3.32938ZM8.29227 9.98815H32.7077V38.2879C32.7077 39.8396 31.485 41.0624 29.9333 41.0624H11.0668C9.51501 41.0624 8.29227 39.8396 8.29227 38.2879V9.98815ZM16.5897 15.5133C16.1486 15.5202 15.7282 15.7019 15.421 16.0185C15.1137 16.3351 14.9447 16.7607 14.951 17.2018V33.8487C14.9479 34.0693 14.9887 34.2883 15.0709 34.493C15.1532 34.6977 15.2753 34.884 15.4302 35.0411C15.5851 35.1982 15.7697 35.323 15.9732 35.4081C16.1767 35.4933 16.3951 35.5371 16.6157 35.5371C16.8363 35.5371 17.0548 35.4933 17.2583 35.4081C17.4618 35.323 17.6464 35.1982 17.8012 35.0411C17.9561 34.884 18.0783 34.6977 18.1605 34.493C18.2428 34.2883 18.2835 34.0693 18.2804 33.8487V17.2018C18.2836 16.979 18.242 16.7578 18.1582 16.5514C18.0743 16.3449 17.9499 16.1574 17.7922 15.9999C17.6345 15.8425 17.4469 15.7183 17.2403 15.6347C17.0338 15.5511 16.8125 15.5098 16.5897 15.5133ZM24.3583 15.5133C23.9171 15.5202 23.4968 15.7019 23.1895 16.0185C22.8823 16.3351 22.7133 16.7607 22.7196 17.2018V33.8487C22.7165 34.0693 22.7572 34.2883 22.8395 34.493C22.9217 34.6977 23.0439 34.884 23.1988 35.0411C23.3536 35.1982 23.5382 35.323 23.7417 35.4081C23.9453 35.4933 24.1637 35.5371 24.3843 35.5371C24.6049 35.5371 24.8233 35.4933 25.0268 35.4081C25.2304 35.323 25.4149 35.1982 25.5698 35.0411C25.7247 34.884 25.8468 34.6977 25.9291 34.493C26.0113 34.2883 26.0521 34.0693 26.049 33.8487V17.2018C26.0522 16.979 26.0106 16.7578 25.9267 16.5514C25.8429 16.3449 25.7184 16.1574 25.5608 15.9999C25.4031 15.8425 25.2154 15.7183 25.0089 15.6347C24.8023 15.5511 24.5811 15.5098 24.3583 15.5133Z" fill="#DC2626" />
                  </svg>
                </div>
                <div class="my-5">
                  <h4 class="text-center text-gray-800 ttp-text-base font-medium leading-normal">
                    {{ textTitle }}
                  </h4>
                  <div class="text-center text-gray-500 ttp-text-xs font-normal leading-normal">
                    {{ textDetail }}
                  </div>
                </div>
              </div>
              <form @submit="submitEvent">
                <div class="flex space-x-2 bg-gray-50 px-5 py-4 text-left dark:bg-gray-700/50">
                  <ButtonDefaultComponent :isSpecial="false" :isVisible="true" :isIcon="true" :isDisable="false" :isSubmit="true" :htmlIcon="''" :title="$t('btn.btn_confirm')" :classEnumName="ENUM_COLOR.RED" :classTagName="'w-full'" :size="ENUM_SIZE.SMALL" @click="handleClick" @touchstart="handleClick" />
                  <ButtonStyle2Component :isSpecial="false" :isVisible="true" :isIcon="true" :isDisable="false" :htmlIcon="''" :title="$t('btn.btn_cancel')" :classEnumName="ENUM_COLOR.DARK" :classTagName="'w-full'" :size="ENUM_SIZE.SMALL" @click="handleCancelClick" @touchstart="handleCancelClick" />
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
